import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import { dealMgmt } from '../../services/dealManagementApi';
import { Badge, Card, Drawer } from '../../components/ui';
import { LoadingState, ErrorState } from '../../features/shared/PageStates';
import { DETAIL_TABS, STAGE_LABELS, STAGE_BADGE, RATING_BADGE, PIPELINE_KANBAN_STAGES } from '../../lib/statusMaps';
import { formatZAR, formatPct, formatDate, humanize } from '../../lib/format';

const OverviewTab = lazy(() => import('../../features/deals/tabs/OverviewTab'));
const Stage1Tab = lazy(() => import('../../features/deals/tabs/Stage1Tab'));
const IcTab = lazy(() => import('../../features/deals/tabs/IcTab'));
const FeeTab = lazy(() => import('../../features/deals/tabs/FeeTab'));
const Stage2Tab = lazy(() => import('../../features/deals/tabs/Stage2Tab'));
const ContractingTab = lazy(() => import('../../features/deals/tabs/ContractingTab'));
const TlQcTab = lazy(() => import('../../features/deals/tabs/TlQcTab'));
const CapitalTab = lazy(() => import('../../features/deals/tabs/CapitalTab'));
const FinanceTab = lazy(() => import('../../features/deals/tabs/FinanceTab'));
const DeliveryTab = lazy(() => import('../../features/deals/tabs/DeliveryTab'));
const RepaymentTab = lazy(() => import('../../features/deals/tabs/RepaymentTab'));
const CloseOutTab = lazy(() => import('../../features/deals/tabs/CloseOutTab'));
const UpdatesTab = lazy(() => import('../../features/deals/tabs/UpdatesTab'));
const ActivityTab = lazy(() => import('../../features/deals/tabs/ActivityTab'));

export function DealDetail({ selectedIds, onNavigate, showToast, user }) {
  const dealId = selectedIds?.dmDealId;
  const initialTab = selectedIds?.dmTab || 'Overview';
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ deal_manager_id: '', team_lead_id: '', reason: '' });
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (selectedIds?.dmTab) setTab(selectedIds.dmTab);
  }, [selectedIds?.dmTab]);

  const load = useCallback(() => {
    if (!dealId) return;
    setLoading(true);
    setError('');
    Promise.all([dealMgmt.getDeal(dealId), dealMgmt.team()])
      .then(([d, t]) => {
        setData(d);
        setTeam(t.users || []);
      })
      .catch(e => {
        setError(e.message || 'Failed to load deal');
        showToast?.(e.message, 'error');
      })
      .finally(() => setLoading(false));
  }, [dealId, showToast]);

  useEffect(() => { load(); }, [load]);

  if (!dealId) {
    return (
      <Card>
        <div style={{ padding: 24, textAlign: 'center' }}>
          Select a deal from the pipeline
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-p btn-sm" onClick={() => onNavigate('dm-pipeline')}>Open pipeline</button>
          </div>
        </div>
      </Card>
    );
  }

  if (loading && !data) return <LoadingState label="Loading deal…" />;
  if (error && !data) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <LoadingState />;

  const { deal } = data;
  const isID = deal.service_type === 'invoice_discounting';
  const stageIdx = PIPELINE_KANBAN_STAGES.indexOf(deal.stage);
  const progressPct = stageIdx >= 0
    ? Math.round(((stageIdx + 1) / PIPELINE_KANBAN_STAGES.length) * 100)
    : (deal.stage === 'closed' ? 100 : 0);

  const assign = async () => {
    if (assigning) return;
    setAssigning(true);
    try {
      await dealMgmt.assignDeal(dealId, assignForm);
      showToast?.('Deal assigned');
      setAssignOpen(false);
      load();
    } catch (e) {
      showToast?.(e.message, 'error');
    } finally {
      setAssigning(false);
    }
  };

  const tabProps = {
    dealId,
    deal,
    data,
    team,
    onReload: load,
    showToast,
    onNavigate,
    user,
  };

  const renderTab = () => {
    switch (tab) {
      case 'Overview': return <OverviewTab {...tabProps} />;
      case 'Stage 1': return <Stage1Tab {...tabProps} />;
      case 'IC': return <IcTab {...tabProps} />;
      case 'Fee': return <FeeTab {...tabProps} />;
      case 'Stage 2 DD': return <Stage2Tab {...tabProps} />;
      case 'Contracting': return <ContractingTab {...tabProps} />;
      case 'Team Lead QC': return <TlQcTab {...tabProps} />;
      case 'Capital Allocation': return <CapitalTab {...tabProps} />;
      case 'Finance': return <FinanceTab {...tabProps} />;
      case 'Delivery': return <DeliveryTab {...tabProps} />;
      case 'Repayment': return <RepaymentTab {...tabProps} />;
      case 'Close-Out': return <CloseOutTab {...tabProps} />;
      case 'Updates': return <UpdatesTab {...tabProps} />;
      case 'Activity Log': return <ActivityTab {...tabProps} />;
      default: return <OverviewTab {...tabProps} />;
    }
  };

  return (
    <div>
      <div className="deal-sticky-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 2 }}>
              <button
                type="button"
                className="btn btn-o btn-sm"
                style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', borderColor: 'rgba(255,255,255,0.25)' }}
                onClick={() => onNavigate('dm-pipeline')}
              >
                ← Pipeline
              </button>
              <span className="mono" style={{ fontSize: 11, opacity: 0.75 }}>{deal.deal_ref}</span>
              <Badge type={STAGE_BADGE[deal.stage] || 'neutral'}>{STAGE_LABELS[deal.stage] || deal.stage}</Badge>
              {deal.end_client_rating && (
                <Badge type={RATING_BADGE[deal.end_client_rating] || 'neutral'}>{deal.end_client_rating}</Badge>
              )}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.25 }}>{deal.client_company_name}</div>
            <div style={{ fontSize: 11.5, opacity: 0.72, marginTop: 2 }}>
              {isID ? 'Invoice Discounting' : 'PO Funding'}
              {deal.end_client_name ? ` · ${deal.end_client_name}` : ''}
              {deal.sub_status ? ` · ${humanize(deal.sub_status)}` : ''}
            </div>
          </div>
          <div style={{ textAlign: 'right', display: 'flex', gap: 16, alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: 9.5, opacity: 0.65, textTransform: 'uppercase' }}>Capital</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{formatZAR(deal.capital_required)}</div>
            </div>
            {!isID && deal.profit_margin_pct != null && (
              <div>
                <div style={{ fontSize: 9.5, opacity: 0.65, textTransform: 'uppercase' }}>Margin</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{formatPct(deal.profit_margin_pct)}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize: 9.5, opacity: 0.65, textTransform: 'uppercase' }}>Progress</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{progressPct}%</div>
            </div>
            <button
              type="button"
              className="btn btn-o btn-sm"
              style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }}
              onClick={() => {
                setAssignForm({
                  deal_manager_id: deal.deal_manager_id || '',
                  team_lead_id: deal.team_lead_id || '',
                  reason: '',
                });
                setAssignOpen(true);
              }}
            >
              Assign
            </button>
          </div>
        </div>

        <div className="deal-sticky-meta">
          <span><strong>DM</strong>{deal.deal_manager_name || '—'}</span>
          <span><strong>TL</strong>{deal.team_lead_name || '—'}</span>
          <span><strong>Risk</strong>{humanize(deal.risk_status) || '—'}</span>
          {deal.funding_cycle_day != null && <span><strong>Funding day</strong>Day {deal.funding_cycle_day}</span>}
          {deal.next_action_due && <span><strong>Next due</strong>{formatDate(deal.next_action_due)}</span>}
        </div>
      </div>

      <div className="next-action-panel">
        <strong>Next action:</strong> {deal.next_required_action || 'No action queued'}
        {deal.next_action_due && (
          <span style={{ marginLeft: 10, color: 'var(--text3)' }}>Due {formatDate(deal.next_action_due)}</span>
        )}
        {deal.deal_manager_name && (
          <span style={{ marginLeft: 10, color: 'var(--text3)' }}>Owner {deal.deal_manager_name}</span>
        )}
      </div>

      <div className="deal-tabs-scroll">
        {DETAIL_TABS.map(t => (
          <button
            key={t}
            type="button"
            className={`deal-tab-btn ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="deal-tab-panel">
        <Suspense fallback={<LoadingState label="Loading tab…" />}>
          {renderTab()}
        </Suspense>
      </div>

      <Drawer open={assignOpen} onClose={() => setAssignOpen(false)} title="Assign deal">
        <div className="form-row">
          <label className="field-label">Team Lead</label>
          <select className="field-input" value={assignForm.team_lead_id} onChange={e => setAssignForm(p => ({ ...p, team_lead_id: e.target.value }))}>
            <option value="">Select…</option>
            {team.map(u => <option key={u.id} value={u.id}>{u.full_name || u.fullName}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label className="field-label">Deal Manager</label>
          <select className="field-input" value={assignForm.deal_manager_id} onChange={e => setAssignForm(p => ({ ...p, deal_manager_id: e.target.value }))}>
            <option value="">Select…</option>
            {team.map(u => <option key={u.id} value={u.id}>{u.full_name || u.fullName}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label className="field-label">Reason</label>
          <input className="field-input" value={assignForm.reason} onChange={e => setAssignForm(p => ({ ...p, reason: e.target.value }))} placeholder="Reassignment reason…" />
        </div>
        <button className="btn btn-p" onClick={assign} disabled={assigning || !assignForm.deal_manager_id} style={{ marginTop: 12 }}>
          {assigning ? 'Saving…' : 'Assign'}
        </button>
      </Drawer>
    </div>
  );
}

export default DealDetail;
